import { Breakpoint, DEFAULT_BREAKPOINT } from '../constants';
import { transformFieldToV1 } from '@wix/form-transformer';
const GRID_DEFAULTS = {
  columns: 12,
  rowHeight: 24,
  margin: {
    vertical: 24,
    horizontal: 24
  },
  padding: {
    vertical: 0,
    horizontal: 0
  }
};
export function createFormView(_ref) {
  var _form$steps, _form$steps2, _layout$small;
  let {
    form,
    path = '',
    stepId,
    recursive = true,
    includeHiddenFields = false,
    isMobile = false,
    breakpoint = DEFAULT_BREAKPOINT,
    useFieldsV2 = false,
    parentId = ''
  } = _ref;
  const step = stepId ? (_form$steps = form.steps) == null ? void 0 : _form$steps.find(_ref2 => {
    let {
      id
    } = _ref2;
    return id === stepId;
  }) : (_form$steps2 = form.steps) == null ? void 0 : _form$steps2[0];
  const layout = (step == null ? void 0 : step.layout) ?? {};
  const fields = form.fields ?? [];
  const platformizedFields = form.formFields;
  let nameView = '';
  if ('properties' in form) {
    var _form$properties;
    nameView = ((_form$properties = form.properties) == null ? void 0 : _form$properties.name) ?? '';
  } else if ('name' in form) {
    nameView = form.name ?? '';
  }
  const hasSmallLayout = (_layout$small = layout.small) == null ? void 0 : _layout$small.items;
  const isSmallBreakpoint = isMobile && hasSmallLayout;
  const fieldViews = (useFieldsV2 && platformizedFields ? platformizedFields : fields).filter(field => includeHiddenFields || !field.hidden).filter(field => {
    var _layout$large;
    return layout == null || (_layout$large = layout.large) == null || (_layout$large = _layout$large.items) == null ? void 0 : _layout$large.find(item => field.id === item.fieldId);
  }).reduce((result, field) => {
    const _field = useFieldsV2 && platformizedFields ? transformFieldToV1(field) : field;
    const fieldView = createFieldView(_field, layout, path, recursive, isMobile, isSmallBreakpoint ? Breakpoint.SMALL : breakpoint);
    if (fieldView) {
      result.push(fieldView);
    }
    return result;
  }, []).sort(sortFieldViewsByColumn).sort(sortFieldViewsByRow);
  const gridView = createGridView(layout, isMobile);
  const formView = {
    parentId,
    name: nameView,
    grid: gridView,
    fields: fieldViews
  };
  if (isMobile && !hasSmallLayout || breakpoint === Breakpoint.SMALL && !hasSmallLayout) {
    return applyMobileLayout(formView);
  }
  return formView;
}
function applyMobileLayout(form) {
  const {
    fields
  } = form.fields.reduce((state, field) => {
    const layout = {
      width: form.grid.columns,
      height: field.layout.height,
      column: 0,
      row: state.row
    };
    state.fields.push({
      ...field,
      layout
    });
    state.row += field.layout.height;
    return state;
  }, {
    row: 0,
    fields: []
  });
  return {
    ...form,
    fields
  };
}
function createFieldView(field, layout, path, recursive, isMobile, breakpoint) {
  var _field$view;
  if (breakpoint === void 0) {
    breakpoint = DEFAULT_BREAKPOINT;
  }
  if (!(field.id && (_field$view = field.view) != null && _field$view.fieldType)) {
    return;
  }
  if (!layout) {
    return;
  }
  const breakpoints = createLayoutBreakpoints(layout, field.id);
  const activeLayout = breakpoints[breakpoint] ?? breakpoints.large;
  if (!activeLayout) {
    return;
  }
  const fieldTarget = field.target || '';
  const fieldPath = path ? `${path}/${fieldTarget}` : fieldTarget;
  const propertiesView = createPropertiesView(field, fieldPath, recursive, isMobile);
  return {
    id: field.id,
    fieldType: field.view.fieldType,
    properties: propertiesView,
    layout: activeLayout,
    layoutByBreakpoint: breakpoints,
    target: fieldTarget,
    path: fieldPath
  };
}
function createLayoutBreakpoints(layout, fieldId) {
  var _layout$large2, _layout$small2;
  const largeItem = (_layout$large2 = layout.large) == null || (_layout$large2 = _layout$large2.items) == null ? void 0 : _layout$large2.find(item => item.fieldId === fieldId);
  const largeLayoutView = largeItem && createLayoutView(largeItem);
  const smallItem = (_layout$small2 = layout.small) == null || (_layout$small2 = _layout$small2.items) == null ? void 0 : _layout$small2.find(item => item.fieldId === fieldId);
  const smallLayoutView = smallItem && createLayoutView(smallItem);
  return {
    large: largeLayoutView,
    small: smallLayoutView
  };
}
export function createPropertiesView(field, path, recursive, isMobile) {
  const {
    hidden,
    pii,
    nestedFormId,
    readOnly
  } = field;
  const {
    disabled,
    defaultValue,
    fieldType,
    form,
    ...rest
  } = field.view ?? {};
  const {
    required
  } = field.validation ?? {};
  const formView = form && recursive ? createFormView({
    form,
    path,
    recursive: false,
    isMobile,
    parentId: field.id
  }) : undefined;
  return {
    disabled: disabled ?? false,
    hidden: hidden ?? false,
    readOnly: readOnly ?? false,
    required: required ?? false,
    pii: pii ?? false,
    defaultValue: defaultValue ?? undefined,
    validation: field.validation ?? {},
    form: formView,
    ...(nestedFormId ? {
      nestedFormId
    } : {}),
    ...rest
  };
}
function createLayoutView(layout) {
  const {
    row,
    column,
    width,
    height
  } = layout;
  if (row === null || row === undefined || column === null || column === undefined || width === null || width === undefined || height === null || height === undefined) {
    return;
  }
  return {
    row,
    column,
    width,
    height,
    static: layout.static
  };
}
function createGridView(layout, isMobile) {
  const {
    columns,
    rowHeight,
    padding,
    margin
  } = (layout == null ? void 0 : layout.large) ?? {};
  let {
    horizontal: marginHorizontal
  } = margin ?? {};
  const {
    vertical: paddingVertical,
    horizontal: paddingHorizontal
  } = padding ?? {};
  const gridRowHeight = rowHeight ?? GRID_DEFAULTS.rowHeight;
  if (isMobile) {
    marginHorizontal = 0;
  }
  return {
    columns: columns ?? GRID_DEFAULTS.columns,
    rowHeight: gridRowHeight,
    margin: {
      vertical: (margin == null ? void 0 : margin.vertical) ?? GRID_DEFAULTS.margin.vertical,
      horizontal: marginHorizontal ?? GRID_DEFAULTS.margin.horizontal
    },
    padding: {
      vertical: paddingVertical ?? GRID_DEFAULTS.padding.vertical,
      horizontal: paddingHorizontal ?? GRID_DEFAULTS.padding.horizontal
    }
  };
}
function sortFieldViewsByRow(fieldA, fieldB) {
  return fieldA.layout.row - fieldB.layout.row;
}
function sortFieldViewsByColumn(fieldA, fieldB) {
  return fieldA.layout.column - fieldB.layout.column;
}
//# sourceMappingURL=form-view.js.map